import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function SubObjectReport({ report }) {
  if (!report) return null;
  if (!report.present) {
    return <p className="text-xs text-muted-foreground">Object not present (type: {report.type}).</p>;
  }
  return (
    <div className="space-y-1 text-xs">
      <div className="flex flex-wrap gap-1">
        {report.keys.map((k) => (
          <Badge key={k} variant="outline" className="text-[10px] font-mono">{k}</Badge>
        ))}
      </div>
      <p>id: <span className="font-mono">{String(report.id)}</span></p>
      {Object.keys(report.name_bearing_field_values || {}).length > 0 ? (
        <pre className="border rounded p-2 overflow-x-auto bg-muted/30 text-[10px]">
          {JSON.stringify(report.name_bearing_field_values, null, 2)}
        </pre>
      ) : (
        <p className="text-amber-600">No name-bearing fields present on this sub-object.</p>
      )}
    </div>
  );
}

function SideReport({ label, side }) {
  if (!side) return null;
  if (side.list_call?.http_status && side.list_call.http_status !== 200) {
    return (
      <div>
        <p className="font-medium text-sm mb-1">{label}</p>
        <p className="text-xs text-red-600">List call failed: HTTP {side.list_call.http_status}</p>
      </div>
    );
  }
  if (side.note) {
    return (
      <div>
        <p className="font-medium text-sm mb-1">{label}</p>
        <p className="text-xs text-muted-foreground">{side.note}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <p className="font-medium text-sm">{label} <span className="font-mono text-xs text-muted-foreground">invoice #{side.sample_invoice_id}</span></p>

      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1">List-response embedded sub-object</p>
        <SubObjectReport report={side.list_response_sub_object} />
      </div>

      {side.detail_call && side.detail_call.http_status === 200 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Detail-endpoint sub-object (Rule 18 check)</p>
          <SubObjectReport report={side.detail_call.sub_object} />
        </div>
      )}

      {side.directory_lookup_by_id && !side.directory_lookup_by_id.skipped && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Directory lookup by id</p>
          {side.directory_lookup_by_id.http_status === 200 ? (
            <pre className="border rounded p-2 overflow-x-auto bg-muted/30 text-[10px]">
              {JSON.stringify(side.directory_lookup_by_id.name_bearing_field_values || {}, null, 2)}
            </pre>
          ) : (
            <p className="text-xs text-red-600">HTTP {side.directory_lookup_by_id.http_status}</p>
          )}
        </div>
      )}

      <div className="p-2 rounded bg-muted/40 text-xs">
        <strong>Mechanism verdict: </strong>{side.mechanism_verdict}
      </div>
    </div>
  );
}

export function PennylaneCounterpartyProbePanel() {
  const [entityId, setEntityId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runProbe = async () => {
    const id = entityId.trim();
    if (!id) {
      toast.error('Enter an entity ID');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneCounterpartyProbe(id);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runProbe();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Pennylane Counterparty Payload Probe</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Fetches one customer invoice and one supplier invoice/expense live and
          reports the raw customer/supplier sub-object shape: keys, name-bearing
          field values, whether the detail endpoint carries more than the list
          endpoint, and whether the customers/suppliers directory resolves the
          id. Read-only. Gate for the counterparty-name fix (CLAUDE.md Prompt 8).
        </p>

        <div className="flex items-center gap-3">
          <input
            type="text"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Entity slug, e.g. bi_sas"
            className="border rounded px-3 py-1.5 text-sm w-72 bg-background"
          />
          <Button onClick={runProbe} disabled={loading || !entityId.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Probe
          </Button>
        </div>

        {result && (
          <div className="space-y-6">
            <p className="text-xs text-muted-foreground">
              {result.entity_name} &middot; checked {result.checked_at}
            </p>
            <SideReport label="Customer invoice (receivable)" side={result.customer_invoice_probe} />
            <SideReport label="Supplier invoice/expense (payable)" side={result.supplier_invoice_probe} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
